import type { SiteDto, SkuDto, StreamEventDto } from '@pl/domain';

/** What a line needs to name a SKU: the office's full SKU and the Wall's catalog-only one both have it. */
export type SkuLabel = Pick<SkuDto, 'code' | 'name' | 'colourName' | 'colourHex'>;
import { shortName } from '../ui/bits';
import { userEventWords } from './userEvents';

/**
 * One line of the live activity feed, in parts rather than as a string: the SKU has to be drawn
 * with its color swatch and its color word (`SkuTag`), and the order reference in mono, so the row
 * needs to know which part is which. `lineText` flattens it for anything that wants plain words.
 */
export type LinePart =
  | { kind: 'who'; text: string }
  | { kind: 'text'; text: string }
  | { kind: 'sku'; sku: SkuLabel | undefined }
  | { kind: 'ref'; text: string };

export type EventDot = 'accent' | 'bad' | 'ok' | 'ink';

const text = (t: string): LinePart => ({ kind: 'text', text: t });

/** What happened, without who or where. Each entry gets the SKU part and the order ref, if any. */
function what(e: StreamEventDto, sku: LinePart | null, ref: LinePart | null): LinePart[] {
  // Settings → Users changes (`user.*`), worded in one place for every screen that shows them.
  const words = userEventWords(e);
  if (words) return [text(words)];
  const item = sku ?? text('a box');
  const onOrder = (word: string) => (ref ? [text(word), ref] : []);
  const order = (verb: string) => (ref ? [text(`${verb} `), ref] : [text(verb)]);
  const p = e.payload;
  switch (e.type) {
    case 'box.picked':
      return ref ? [item, text(' → '), ref] : [text('Picked '), item, text(' for a transfer')];
    case 'box.received':
      return [text('Received '), item];
    case 'box.received_transfer':
      return [text('Received '), item, text(' off a transfer')];
    case 'box.unpicked':
      return [text('Put back '), item, ...onOrder(' from ')];
    case 'box.shipped':
      return [text('Shipped '), item, ...onOrder(' on ')];
    case 'box.sent':
      return [text('Sent '), item, text(' on a transfer')];
    case 'box.written_off':
      return [text('Wrote off '), item];
    case 'box.found':
      return [text('Found '), item];
    case 'box.voided':
      return [text('Voided the sticker for '), item];
    case 'scan.rejected':
      if (p.reasonCode === 'WRONG_SKU') return [text('Wrong color: '), item, ...onOrder(' for ')];
      return [text(typeof p.message === 'string' && p.message ? p.message : 'Scan refused')];
    case 'scan.override':
      return [text('Override: '), item, ...onOrder(' for ')];
    case 'order.created':
      return order('New order');
    case 'order.updated':
      return order('Changed order');
    case 'order.shipped':
      return order('Shipped order');
    case 'order.cancelled':
      return order('Cancelled order');
    case 'label.batch_created':
      return [text(typeof p.qty === 'number' ? `Printed ${p.qty} ${p.qty === 1 ? 'sticker' : 'stickers'}` : 'Printed stickers')];
    case 'label.batch_voided':
      return [text('Voided a sticker batch')];
    case 'label.reprinted':
      return [text('Reprinted a sticker')];
    case 'pallet.activated':
      return [text('Started a pallet')];
    case 'import.applied':
      return [text('Imported the catalog')];
    case 'transfer.created':
      return [text('Created a transfer')];
    case 'transfer.sent':
      return [text('Sent a transfer')];
    case 'transfer.closed':
      return [text('Closed a transfer')];
    case 'transfer.cancelled':
      return [text('Canceled a transfer')];
    case 'count.started':
      return [text('Started a count')];
    case 'count.finished':
      return [text('Finished a count')];
    case 'count.cancelled':
      return [text('Canceled a count')];
    case 'discrepancy.resolved':
      return [text('Resolved a discrepancy')];
    default:
      return [text('Other activity')];
  }
}

/** What happened, as parts, without who or where: `C6 CMR Blue → TS-10452`. The Wall shows who apart. */
export function describeAction(e: StreamEventDto, skuMap: Map<string, SkuLabel>): LinePart[] {
  const sku: LinePart | null = e.skuId ? { kind: 'sku', sku: skuMap.get(e.skuId) } : null;
  const ref: LinePart | null = e.orderRef ? { kind: 'ref', text: e.orderRef } : null;
  return what(e, sku, ref);
}

/** `Ali K. · CA · C6 CMR Blue → TS-10452`, as parts. Who and where are left out when unknown. */
export function describeEvent(e: StreamEventDto, skuMap: Map<string, SkuLabel>, siteMap: Map<string, Pick<SiteDto, 'shortCode'>>): LinePart[] {
  const site = e.siteId ? (siteMap.get(e.siteId)?.shortCode ?? null) : null;
  const head: LinePart[] = [];
  if (e.actorName) head.push({ kind: 'who', text: e.actorName });
  if (site) head.push(text(head.length ? ` · ${site}` : site));
  if (head.length) head.push(text(' · '));
  return [...head, ...describeAction(e, skuMap)];
}

const skuWords = (sku: SkuLabel | undefined) => (sku ? [shortName(sku.name), sku.colourName].filter(Boolean).join(' ') : 'Blank sticker');

export function lineText(parts: LinePart[]): string {
  return parts.map((p) => (p.kind === 'sku' ? skuWords(p.sku) : p.text)).join('');
}

/** Accent for a pick, red for a refused scan, green for a box arriving, ink for everything else. */
export function eventDot(e: StreamEventDto): EventDot {
  if (e.type === 'box.picked') return 'accent';
  if (e.type === 'scan.rejected') return 'bad';
  if (e.type === 'box.received' || e.type === 'box.received_transfer') return 'ok';
  return 'ink';
}
